import { benchSeatTopMm } from "./benchHeight";
import { BENCH_SLAT_THICKNESS_MM } from "./constants";
import { WALL_H_M } from "../geometry/buildingDimensions";
import {
	LOWER_BENCH_TOP_BELOW_WALL_TOP_M,
	UPPER_BENCH_TOP_BELOW_WALL_TOP_M,
	benchLegCount,
	benchLegHeightMm,
} from "./constants";
import {
	type BenchRun,
	type InteriorRoom,
	type InteriorWall,
	wallAxisLengthMm,
	wallRunsAlongX,
} from "./types";

/**
 * Planning envelope for the end-supported lower-bench arrangement. This is
 * NOT a load rating for the frame or its fixings. Longer runs retain floor
 * support until a separately dimensioned bench system is selected.
 */
export const END_WALL_BENCH_PLANNING_LIMIT_MM = 2400;

/** End contact is checked after heater clipping, in the finished room frame. */
export function hasEndWallSupports(
	run: BenchRun,
	room?: InteriorRoom,
): boolean {
	const endWalls: InteriorWall[] = wallRunsAlongX(run.wall)
		? ["left", "right"]
		: ["back", "front"];
	return Boolean(
		room &&
			room.id === run.room &&
			(run.supportMode === "endWalls" ||
				(run.room === "sauna" &&
					run.tier === "lower" &&
					run.legHeightMm === undefined)) &&
			!endWalls.some(
				(wall) =>
					room.benchSupportExcludedWalls?.includes(wall) ||
					room.glazedWalls?.includes(wall),
			) &&
			run.lengthMm <= END_WALL_BENCH_PLANNING_LIMIT_MM &&
			Math.abs(run.startMm) <= 0.5 &&
			Math.abs(run.startMm + run.lengthMm - wallAxisLengthMm(room, run.wall)) <=
				0.5,
	);
}

/** Explicit support feasibility uses finished contact and built opening evidence. */
export function benchSupportFits(run: BenchRun, room?: InteriorRoom): boolean {
	if (!run.supportMode || run.supportMode === "floor") return true;
	if (run.supportMode === "endWalls") return hasEndWallSupports(run, room);
	return (
		room?.id === run.room &&
		run.offsetFromWallMm === 0 &&
		!room?.benchSupportExcludedWalls?.includes(run.wall) &&
		!room?.glazedWalls?.includes(run.wall)
	);
}

/** Same support and timber lengths for the renderer, cuts and material quantities. */
export function resolveBenchSupport(run: BenchRun, room?: InteriorRoom) {
	if (run.supportMode) {
		// Invalid wall choices fall back to floor support for externally loaded designs;
		// validation rejects the edit instead of drawing an unsupported assembly.
		const support = benchSupportFits(run, room) ? run.supportMode : "floor";
		const legHeightMm =
			support === "wall" || support === "endWalls"
				? 0
				: benchSeatTopMm(run, room?.wallHeightMm) - BENCH_SLAT_THICKNESS_MM;
		return {
			support,
			legHeightMm,
			legCount:
				legHeightMm > 0
					? benchLegCount(run.lengthMm) * (support === "floor" ? 2 : 1)
					: 0,
		};
	}
	// Being in front of the upper bench does not make a run freestanding:
	// it may still bear on the two perpendicular side walls (Tylö Classic).
	if (hasEndWallSupports(run, room)) {
		return { support: "endWalls", legHeightMm: 0, legCount: 0 } as const;
	}
	const detached = run.offsetFromWallMm > 0;
	const wallHeightM =
		room?.wallHeightMm !== undefined ? room.wallHeightMm / 1000 : WALL_H_M;
	const defaultLegHeightMm =
		run.tier === "lower"
			? benchLegHeightMm(wallHeightM, LOWER_BENCH_TOP_BELOW_WALL_TOP_M)
			: detached
				? benchLegHeightMm(wallHeightM, UPPER_BENCH_TOP_BELOW_WALL_TOP_M)
				: 0;
	const legHeightMm =
		run.seatHeightMm !== undefined &&
		(defaultLegHeightMm > 0 || (run.legHeightMm ?? 0) > 0)
			? benchSeatTopMm(run, wallHeightM * 1000) - BENCH_SLAT_THICKNESS_MM
			: detached && (run.legHeightMm ?? 0) <= 0
				? defaultLegHeightMm
				: (run.legHeightMm ?? defaultLegHeightMm);
	const support = detached
		? "floor"
		: legHeightMm > 0
			? "wall+frontLegs"
			: "wall";
	return {
		support,
		legHeightMm,
		legCount:
			legHeightMm > 0 ? benchLegCount(run.lengthMm) * (detached ? 2 : 1) : 0,
	} as const;
}
